<template>
  <div class="ot-root">
    <!-- ── TOPNAV (sutil) ── -->
    <header class="ot-topnav">
      <span class="ot-topnav-brand">ObraTycoon</span>
      <nav class="ot-topnav-actions">
        <button class="tn-btn" type="button" @click="goEntrar">Login</button>
        <button class="tn-btn tn-btn--primary" type="button" @click="goRegistro">Registro</button>
      </nav>
    </header>

    <!-- ── STAGE ── -->
    <main class="stage">
      <h1 class="wordmark" aria-label="ObraTycoon">
        <span class="obra">
          <span
            v-for="(ch, i) in 'Obra'"
            :key="'o' + i"
            class="ch"
            :style="{ animationDelay: (0.25 + i * 0.045).toFixed(3) + 's' }"
          >{{ ch }}</span>
        </span>
        <span class="tycoon">
          <span
            v-for="(ch, i) in 'Tycoon'"
            :key="'t' + i"
            class="ch"
            :style="{ animationDelay: (0.25 + (4 + i) * 0.045).toFixed(3) + 's' }"
          >{{ ch }}</span>
        </span>
      </h1>

      <nav class="actions">
        <button class="btn is-primary" type="button" @click="goPlanos">Planos</button>
        <button class="btn is-quiet" type="button" @click="goSaiba">Saiba mais</button>
      </nav>
    </main>

    <!-- ── FOOTER ── -->
    <footer class="footer">
      <router-link class="footer-legal-link" to="/termos">Termo de Uso</router-link>
      <span class="footer-dot">·</span>
      <router-link class="footer-legal-link" to="/privacidade">Política de Privacidade</router-link>
      <span class="footer-dot">·</span>
      <a class="footer-gus" href="https://www.gusmachado.com.br/" target="_blank" rel="noopener">by GusMachado</a>
    </footer>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

export default defineComponent({
  name: 'LandingView',
  setup() {
    const router = useRouter()

    // Cada "página" da apresentação é uma rota própria
    const goPlanos   = () => router.push('/planos')
    const goSaiba    = () => router.push('/saiba-mais')
    const goEntrar   = () => router.push('/entrar')
    const goRegistro = () => router.push('/registro')

    // ── Lifecycle ──
    let fontLink: HTMLLinkElement | null = null
    onMounted(() => {
      fontLink = document.createElement('link')
      fontLink.rel = 'stylesheet'
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600;700&display=swap'
      document.head.appendChild(fontLink)
      document.body.style.overflow = 'hidden'
    })
    onUnmounted(() => {
      if (fontLink && document.head.contains(fontLink)) document.head.removeChild(fontLink)
      document.body.style.overflow = ''
    })

    return { goPlanos, goSaiba, goEntrar, goRegistro }
  }
})
</script>

<style scoped lang="scss">
// ── CSS custom properties ─────────────────────────────────────────────────
.ot-root {
  --bg:          oklch(0.44 0.118 253);
  --bg-2:        oklch(0.36 0.105 254);
  --ink:         oklch(0.98 0.012 250);
  --ink-soft:    oklch(0.98 0.012 250 / 0.66);
  --ink-faint:   oklch(0.98 0.012 250 / 0.42);
  --accent:      oklch(0.78 0.15 68);
  --grid:        oklch(1 0 0 / 0.16);
  --grid-minor:  oklch(1 0 0 / 0.07);
  --line:        oklch(1 0 0 / 0.32);
  --dur:         600ms;
}

// ── Root wrapper ──────────────────────────────────────────────────────────
.ot-root {
  font-family: "Outfit", system-ui, sans-serif;
  background: var(--bg);
  color: var(--ink);
  min-height: 100svh;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  position: relative;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;

  // Blueprint grid
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(to right, var(--grid) 1px, transparent 1px),
      linear-gradient(to bottom, var(--grid) 1px, transparent 1px),
      linear-gradient(to right, var(--grid-minor) 1px, transparent 1px),
      linear-gradient(to bottom, var(--grid-minor) 1px, transparent 1px);
    background-size: 96px 96px, 96px 96px, 24px 24px, 24px 24px;
    background-position: center;
    -webkit-mask-image: radial-gradient(ellipse 92% 88% at 50% 50%, #000 60%, transparent 100%);
    mask-image: radial-gradient(ellipse 92% 88% at 50% 50%, #000 60%, transparent 100%);
    pointer-events: none;
    z-index: 0;
  }

  // Vignette overlay
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 70% 62% at 50% 48%, transparent 45%, var(--bg-2) 100%);
    pointer-events: none;
    z-index: 0;
  }
}

// ── Stage ─────────────────────────────────────────────────────────────────
.stage {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(28px, 4vh, 46px);
  padding: 6vw;
  text-align: center;
}

// ── Wordmark ──────────────────────────────────────────────────────────────
.wordmark {
  font-size: clamp(46px, 9.2vw, 132px);
  line-height: 1;
  letter-spacing: -0.035em;
  display: flex;
  align-items: baseline;
  white-space: nowrap;
}

.obra   { font-weight: 300; color: var(--ink); }
.tycoon { font-weight: 600; color: var(--ink); }

.ch { display: inline-block; }

// ── Buttons ───────────────────────────────────────────────────────────────
.actions {
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.6vw, 20px);
  margin-top: clamp(4px, 1vh, 12px);
}

.btn {
  font-family: inherit;
  font-size: clamp(13px, 1.05vw, 15px);
  font-weight: 500;
  letter-spacing: 0.04em;
  color: var(--ink);
  background: transparent;
  border: 1px solid var(--line);
  border-radius: 4px;
  padding: 0.72em 1.6em;
  cursor: pointer;
  text-decoration: none;
  line-height: 1;
  white-space: nowrap;
  transition: border-color 0.25s ease, color 0.25s ease, background 0.25s ease, transform 0.25s ease;

  &:hover {
    border-color: oklch(1 0 0 / 0.6);
    background: oklch(1 0 0 / 0.06);
    transform: translateY(-1px);
  }

  &.is-primary {
    color: var(--bg);
    background: var(--ink);
    border-color: var(--ink);

    &:hover {
      background: oklch(0.92 0.012 250);
      border-color: oklch(0.92 0.012 250);
    }
  }

  &.is-quiet {
    border-color: transparent;
    color: var(--ink-soft);
    padding-left: 0.6em;
    padding-right: 0.6em;

    &:hover {
      color: var(--ink);
      border-color: transparent;
      background: transparent;
    }
  }
}

// ── Footer ────────────────────────────────────────────────────────────────
.footer {
  position: fixed;
  bottom: clamp(18px, 3vh, 30px);
  left: 0;
  right: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.12em;
  color: var(--ink-faint);
  white-space: nowrap;
  pointer-events: none;
}
.footer-gus,
.footer-legal-link {
  pointer-events: auto;
  color: var(--ink-faint);
  text-decoration: none;
  transition: color .18s;
  &:hover { color: var(--ink); text-decoration: underline; }
}
.footer-dot {
  opacity: .6;
}

/* ── Topnav sutil ── */
.ot-topnav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 5;
  display: flex; align-items: center; justify-content: space-between;
  padding: clamp(14px, 2.4vh, 22px) clamp(18px, 4vw, 44px);
}
.ot-topnav-brand {
  font-size: 15px; font-weight: 700; letter-spacing: .02em;
  color: var(--ink-soft);
}
.ot-topnav-actions { display: flex; gap: 10px; }
.tn-btn {
  padding: 7px 16px; border-radius: 999px; cursor: pointer;
  font-size: 13px; font-weight: 600; font-family: inherit;
  background: transparent; color: var(--ink-soft);
  border: 1px solid color-mix(in oklch, var(--ink) 22%, transparent);
  transition: background .18s, color .18s, border-color .18s;
  &:hover { color: var(--ink); border-color: color-mix(in oklch, var(--ink) 42%, transparent); }
  &--primary {
    background: var(--ink); color: var(--bg); border-color: var(--ink);
    &:hover { color: var(--bg); opacity: .9; }
  }
}
@media (max-width: 560px) {
  .ot-topnav-brand { font-size: 13px; }
  .tn-btn { padding: 6px 13px; font-size: 12px; }
}

// ── Animations ────────────────────────────────────────────────────────────
@keyframes ch-in {
  from { transform: translateY(0.2em); }
  to   { transform: translateY(0); }
}

@keyframes fade-up {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: no-preference) {
  .ch      { animation: ch-in 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
  .actions { animation: fade-up 0.9s ease 0.55s both; }
  .footer  { animation: fade-up 0.9s ease 0.8s both; }
}
</style>
